import React, { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { logError } from "../lib/log";
import { BellRing, Mail } from "lucide-react";

function useAbsences() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get("/teacher/absences").then((r) => setData(r.data)).catch((e) => logError("Absences load failed", e)); }, []);
  const notify = async () => {
    setStatus("Sending…");
    try {
      const { data: r } = await api.post("/teacher/absences/notify");
      setStatus(r.sent ? `Emailed you about ${r.flagged} student(s).` : "Nothing new to send — you've already been alerted.");
    } catch (e) {
      setStatus(apiError(e, "Could not send"));
    }
  };
  return { data, status, notify };
}

function AbsenceList({ list }) {
  if (list.length === 0) return null;
  return (
    <ul className="mt-3 divide-y divide-white/5 rounded-md border border-white/10 bg-black/40">
      {list.map((s) => (
        <li key={s.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`absence-row-${s.id}`}>
          <div className="min-w-0">
            <div className="truncate font-medium text-white">{s.name}</div>
            <div className="truncate text-xs text-slate-500">{s.last_active ? `Last active ${s.last_active}` : "Never attempted"}</div>
          </div>
          <span className="shrink-0 rounded-full border border-amber-400/40 bg-amber-500/10 px-2 py-0.5 font-mono-num text-xs text-amber-200">{s.missed_days}{s.missed_days >= 30 ? "+" : ""} days</span>
        </li>
      ))}
    </ul>
  );
}

export function AbsenceWatch() {
  const { data, status, notify } = useAbsences();
  if (!data) return null;
  const list = data.students || [];
  return (
    <div className="rounded-md border border-amber-400/30 bg-amber-500/5 p-4 sm:p-5" data-testid="absence-watch">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-300"><BellRing size={12} /> Absence alerts</div>
          <div className="font-display mt-1 text-lg font-semibold text-white">
            <span className="font-mono-num text-amber-200" data-testid="absence-count">{list.length}</span> student{list.length === 1 ? "" : "s"} missed {data.threshold}+ daily challenges in a row
          </div>
        </div>
        <button onClick={notify} data-testid="absence-notify-btn" className="inline-flex items-center gap-2 rounded-md border border-amber-400/40 bg-black/50 px-3 py-2 text-xs font-medium text-amber-100 hover:bg-black/70">
          <Mail size={12} /> Email me now
        </button>
      </div>
      <p className="mt-1 text-xs text-slate-400">Checked automatically every day at 20:00 UTC — you get one quiet email per absence streak.</p>
      {status && <div className="mt-2 text-xs text-amber-200" data-testid="absence-status">{status}</div>}
      <AbsenceList list={list} />
    </div>
  );
}
